import { useState, useCallback } from 'react';
import { screenToCanvas } from './utils/canvasUtils';

/**
 * useCampaignDrop Hook
 * 
 * Handles drag-drop of campaigns from the sidebar onto the flowchart canvas.
 * 
 * Two modes:
 * 1. Single campaign mode: Nests the dropped campaign into the current campaign
 * 2. Multi-campaign mode: Adds the dropped campaign to the view's scope
 */
export const useCampaignDrop = ({
  containerRef,
  pan,
  zoom,
  campaignId,
  onNestCampaign,
  // Multi-campaign mode props
  isMultiCampaignMode = false,
  onAddCampaignToView,
  currentCampaignIds = [],
}) => {
  // Track cursor position for the drop preview box (single campaign mode)
  const [campaignDragPos, setCampaignDragPos] = useState(null);
  // Track if we're dragging over in multi-campaign mode (for full-canvas highlight)
  const [isMultiCampaignDragOver, setIsMultiCampaignDragOver] = useState(false);
  // Track the dragged campaign ID for validation
  const [draggedCampaignId, setDraggedCampaignId] = useState(null);

  const handleDragOver = useCallback((e) => {
    // Only handle campaign drags from sidebar
    if (!e.dataTransfer.types.includes('application/fokus-campaign')) return;
    
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (isMultiCampaignMode) {
      // In multi-campaign mode, show full-canvas highlight
      setIsMultiCampaignDragOver(true);
      // Try to get the dragged ID from the drag event (may not be available until drop)
      // We'll validate on drop
    } else {
      // In single campaign mode, track cursor position for preview box
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setCampaignDragPos({ 
          x: e.clientX - rect.left, 
          y: e.clientY - rect.top 
        });
      }
    }
  }, [containerRef, isMultiCampaignMode]);

  const handleDragLeave = useCallback((e) => {
    // Only clear if actually leaving the container (not entering a child)
    if (!containerRef.current?.contains(e.relatedTarget)) {
      setCampaignDragPos(null);
      setIsMultiCampaignDragOver(false);
      setDraggedCampaignId(null);
    }
  }, [containerRef]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setCampaignDragPos(null);
    setIsMultiCampaignDragOver(false);
    setDraggedCampaignId(null);
    
    const draggedSprintId = e.dataTransfer.getData('application/fokus-campaign');
    if (!draggedSprintId) return;
    
    if (isMultiCampaignMode) {
      // Multi-campaign mode: Add campaign to view
      if (!onAddCampaignToView) {
        console.warn('[FLOW-DROP] No onAddCampaignToView handler in multi-campaign mode');
        return;
      }
      
      // Check if campaign is already in the view
      if (currentCampaignIds.includes(draggedSprintId)) {
        console.log('[FLOW-DROP] Campaign already in view:', draggedSprintId);
        return;
      }
      
      console.log('[FLOW-DROP] Adding campaign to multi-campaign view:', draggedSprintId);
      onAddCampaignToView(draggedSprintId);
    } else {
      // Single campaign mode: Nest campaign
      if (!onNestCampaign || !campaignId) return;
      
      // Validation: Can't drop a campaign into itself
      if (draggedSprintId === campaignId) {
        console.warn('[FLOW-DROP] Cannot nest a campaign into itself');
        return;
      }
      
      // Convert screen coordinates to canvas coordinates
      const canvasPos = screenToCanvas(e.clientX, e.clientY, containerRef, pan, zoom);
      
      // Offset by half the default node dimensions so the node is centered on cursor
      const NODE_WIDTH = 288;
      const NODE_HEIGHT = 162;
      const centeredPos = {
        x: canvasPos.x - NODE_WIDTH / 2,
        y: canvasPos.y - NODE_HEIGHT / 2,
      };
      
      onNestCampaign(draggedSprintId, campaignId, null, { position: centeredPos });
      
      console.log('[FLOW-DROP] Campaign dropped at canvas position:', centeredPos);
    }
  }, [onNestCampaign, campaignId, containerRef, pan, zoom, isMultiCampaignMode, onAddCampaignToView, currentCampaignIds]);

  return {
    // Single campaign mode
    campaignDragPos,
    // Multi-campaign mode
    isMultiCampaignDragOver,
    // Handlers
    handleCampaignDragOver: handleDragOver,
    handleCampaignDragLeave: handleDragLeave,
    handleCampaignDrop: handleDrop,
  };
};
